import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Text,
  BlockStack,
  TextField,
  Autocomplete,
  DropZone,
  Icon,
  InlineStack,
} from '@shopify/polaris';
import {
  UploadMajor,
  MagicMajor,
  SearchMinor,
} from '@shopify/polaris-icons';
import { BlobServiceClient } from '../../services/blobStorageCompat';
import { trialsService } from '../../services/trialsService';
import { generationsService } from '../../services/generationsService';
import { authService } from '../../services/authService';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const CreateTrialModal = ({ 
  isOpen, 
  onClose, 
  setToastMessage, 
  setToastActive,
  onTrialCreated 
}) => {
  const [isCreatingTrial, setIsCreatingTrial] = useState(false);
  const [trialFiles, setTrialFiles] = useState([]);
  const [newTrialData, setNewTrialData] = useState({
    kid_name: '',
    story: ''
  });
  const [azureSettings, setAzureSettings] = useState(null);
  
  // Story dropdown states
  const [selectedStoryForTrial, setSelectedStoryForTrial] = useState([]);
  const [stories, setStories] = useState([]);
  const [isLoadingStories, setIsLoadingStories] = useState(false);
  const [storyAutocompleteInputValue, setStoryAutocompleteInputValue] = useState('');
  const [storyAutocompleteOptions, setStoryAutocompleteOptions] = useState([]);

  // Upload file to Azure Blob using Azure SDK
  const uploadFileToAzure = useCallback(async (file, kidName, story, settings = null) => {
    const currentSettings = settings || azureSettings;
    if (!currentSettings) {
      throw new Error('Azure settings not loaded');
    }
    const { AZURE_BLOB_BASE_URL, AZURE_SAS_TOKEN } = currentSettings;
    const containerUrl = AZURE_BLOB_BASE_URL + AZURE_SAS_TOKEN;
    const blobServiceClient = new BlobServiceClient(containerUrl);
    const containerClient = blobServiceClient.getContainerClient("");
    
    // Sanitize and build the new file name
    const sanitize = str => (str || '').replace(/[^a-zA-Z0-9]/g, '_');
    const ext = file.name.split('.').pop();
    // Generate RandomTXT: short random string + timestamp
    const randomTxt = `${Math.random().toString(36).substring(2, 6)}${Date.now()}`;
    const newFileName = `${sanitize(randomTxt)}_${sanitize(kidName)}_${sanitize(story)}.${ext}`;
    
    // Upload to faces folder for trials
    const blobName = `faces/${newFileName}`;
    const blockBlobClient = containerClient.getBlockBlobClient(blobName);
    const { publicUrl } = await blockBlobClient.uploadBrowserData(file, {
      blobHTTPHeaders: { blobContentType: file.type }
    });
    return publicUrl;
  }, [azureSettings]);

  // Fetch stories for dropdown
  const fetchStories = useCallback(async () => {
    setIsLoadingStories(true);
    try {
      const data = await generationsService.getAllGenerationStories();
      setStories(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error('Error fetching stories:', err);
      setStories([]);
    } finally {
      setIsLoadingStories(false);
    }
  }, []);

  // Handler for story autocomplete change
  const handleStoryAutocompleteChange = useCallback((value) => {
    setStoryAutocompleteInputValue(value);
    if (value === '') {
      const allOptions = stories.map(story => ({ label: story.title, value: story.slug }));
      setStoryAutocompleteOptions(allOptions);
      return;
    }
    const filterRegex = new RegExp(value, 'i');
    const filteredOptions = stories
      .filter(story => story.title.match(filterRegex) || story.slug.match(filterRegex))
      .map(story => ({ label: story.title, value: story.slug }));
    setStoryAutocompleteOptions(filteredOptions);
  }, [stories]);

  // Handler for story autocomplete select
  const handleStoryAutocompleteSelect = useCallback((selected) => {
    setSelectedStoryForTrial(selected);
    const selectedSlug = selected[0] || '';
    setNewTrialData(prev => ({ ...prev, story: selectedSlug }));
    const selectedStory = stories.find(story => story.slug === selectedSlug);
    setStoryAutocompleteInputValue(selectedStory ? selectedStory.title : '');
  }, [stories]);

  // Load Azure settings from API (using same approach as AI Design pages)
  const loadAzureSettings = useCallback(async () => {
    try {
      const response = await fetch(`${SERVER_URL}/settings/storage/client`, {
        headers: authService.getAuthHeaders(),
      });
      
      if (!response.ok) {
        console.error('Failed to load Azure settings:', response.status);
        return null;
      }
      
      const result = await response.json();
      if (result?.success && result?.data && result.data.AZURE_BLOB_BASE_URL && result.data.AZURE_SAS_TOKEN) {
        setAzureSettings(result.data);
        return result.data;
      }
      
      console.warn('Azure settings response missing required fields:', result);
      return null;
    } catch (error) {
      console.error('Error loading Azure settings:', error);
      return null;
    }
  }, []);

  // Load Azure settings and stories when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAzureSettings();
      fetchStories();
    }
  }, [isOpen, loadAzureSettings, fetchStories]);

  // Initialize autocomplete options when stories changes
  useEffect(() => {
    setStoryAutocompleteOptions(stories.map(story => ({ label: story.title, value: story.slug })));
  }, [stories]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNewTrialData({ kid_name: '', story: '' });
      setTrialFiles([]);
      setSelectedStoryForTrial([]);
      setStoryAutocompleteInputValue('');
    }
  }, [isOpen]);

  // Handle file drop for trial images
  const handleTrialFilesDrop = useCallback((files) => {
    setTrialFiles(files);
  }, []);

  // Handle trial creation
  const handleCreateTrial = useCallback(async () => {
    if (!newTrialData.kid_name.trim()) {
      setToastMessage('Kid name is required');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      return;
    }

    if (!newTrialData.story.trim()) {
      setToastMessage('Please select a story');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      return;
    }

    if (trialFiles.length === 0) {
      setToastMessage('At least one kid image is required');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      return;
    }

    setIsCreatingTrial(true);
    try {
      // Ensure Azure settings are loaded before uploading
      let currentAzureSettings = azureSettings;
      if (!currentAzureSettings) {
        setToastMessage('Loading Azure settings, please wait...');
        setToastActive(true);
        setTimeout(() => setToastActive(false), 2000);
        
        currentAzureSettings = await loadAzureSettings();
        
        // Wait a bit more and try again if still not loaded
        if (!currentAzureSettings) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          currentAzureSettings = await loadAzureSettings();
        }
        
        if (!currentAzureSettings) {
          throw new Error('Failed to load Azure settings. Please try again.');
        }
      }
      
      // Upload all files to Azure
      const uploadPromises = trialFiles.map(file => 
        uploadFileToAzure(file, newTrialData.kid_name, newTrialData.story, currentAzureSettings)
      );
      const uploadedUrls = await Promise.all(uploadPromises);

      // Create trial with uploaded image URLs
      const trialData = {
        kid_name: newTrialData.kid_name.trim(),
        kid_images: uploadedUrls,
        story: newTrialData.story.trim()
      };

      await trialsService.createTrial(trialData);

      setToastMessage('Trial created successfully');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);

      // Reset form
      setNewTrialData({ kid_name: '', story: '' });
      setTrialFiles([]);
      setSelectedStoryForTrial([]);
      setStoryAutocompleteInputValue('');

      // Close modal and refresh list
      onClose();
      if (onTrialCreated) {
        onTrialCreated();
      }
    } catch (error) {
      console.error('Error creating trial:', error);
      setToastMessage(`Error creating trial: ${error.message}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 4000);
    } finally {
      setIsCreatingTrial(false);
    }
  }, [newTrialData, trialFiles, uploadFileToAzure, onClose, onTrialCreated, setToastMessage, setToastActive, azureSettings, loadAzureSettings]);

  const handleClose = () => {
    if (!isCreatingTrial) {
      setNewTrialData({ kid_name: '', story: '' });
      setTrialFiles([]);
      setSelectedStoryForTrial([]);
      setStoryAutocompleteInputValue('');
      onClose();
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title="Create New Trial"
      primaryAction={{
        content: 'Create Trial',
        onAction: handleCreateTrial,
        loading: isCreatingTrial,
        icon: MagicMajor,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: handleClose,
          disabled: isCreatingTrial,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <TextField
            label="Kid Name"
            value={newTrialData.kid_name}
            onChange={(value) => setNewTrialData(prev => ({ ...prev, kid_name: value }))}
            placeholder="Enter kid's name"
            disabled={isCreatingTrial}
            requiredIndicator
          />

          <Autocomplete
            options={storyAutocompleteOptions}
            selected={selectedStoryForTrial}
            onSelect={handleStoryAutocompleteSelect}
            loading={isLoadingStories}
            textField={
              <Autocomplete.TextField
                onChange={handleStoryAutocompleteChange}
                label="Story"
                value={storyAutocompleteInputValue}
                prefix={<Icon source={SearchMinor} tone="base" />}
                placeholder="Search for a story..."
                autoComplete="off"
                disabled={isCreatingTrial}
                requiredIndicator
              />
            }
          />

          <div>
            <Text variant="bodyMd" fontWeight="medium">
              Kid Images
            </Text>
            <Text variant="bodySm" tone="subdued" style={{ marginBottom: '8px' }}>
              Upload one or more images of the kid
            </Text>
            <DropZone
              onDrop={handleTrialFilesDrop}
              accept="image/*"
              type="image"
              allowMultiple
              disabled={isCreatingTrial}
            >
              <DropZone.FileUpload
                actionTitle="Add images"
                actionHint="or drop images to upload"
              />
            </DropZone>
            {trialFiles.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <Text variant="bodyMd" fontWeight="medium">
                  Selected Images ({trialFiles.length})
                </Text>
                <div style={{ marginTop: '8px' }}>
                  {trialFiles.map((file, index) => (
                    <InlineStack key={index} gap="200" align="center" style={{ marginBottom: '4px' }}>
                      <Icon source={UploadMajor} />
                      <Text variant="bodySm">{file.name}</Text>
                      <Text variant="bodySm" tone="subdued">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </Text>
                    </InlineStack>
                  ))}
                </div>
              </div>
            )}
          </div>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default CreateTrialModal;
